/*
 * Mix & Match announcement bar (sections/mix-match-announcement-bar.liquid).
 *
 * Liquid renders the first paint (count, message, reached steps, link) and a
 * config with every message bucket already filled except the per-count
 * placeholders. This file only re-counts the cart and picks the bucket:
 *
 *   cart:updated        the theme's cart drawer hands over the fresh cart
 *   ajaxProduct:added   theme / Mix & Match v3 adds; /cart.js if no cart:updated follows
 *   /cart/* requests    fetch + XHR from apps that fire no theme event (setting)
 *   pageshow            back/forward cache
 *   mm3:selection       the PDP box saved / cleared its kit selection
 *   storage             the same, from another tab
 *
 * Link ("Checkout →", links.top) as soon as the kits IN THE CART reach the
 * first discount tier (e.g. 2 kits). When a tier is only reached with kits
 * picked in the Build Your Own box / PLP tray that aren't added yet, it
 * reads "Add kits to cart →" (links.pending) instead: a click adds the saved
 * bundle (its kits and add-ons, with the same _mix_match / _mix_match_addon
 * properties as the PDP box and PLP tray) in one /cart/add.js request, clears
 * it, and opens the cart slider (UpCart, or the theme drawer) like any Add to
 * cart. If the add fails, the reason is shown under the bar.
 *
 * The count is cart kits + kits in the saved bundle, shared by the PDP Build
 * Your Own box (mix-match-v3.js) and the PLP tray (plp-byob.js): kept 24h in
 * localStorage, cleared once added to cart. So the bar, the PDP box and the
 * PLP tray / banner all show the same count while choosing.
 *
 * Membership (pricing rule 8) is decided in Liquid only. A cart line whose
 * product isn't in config.known (cart + group collection at render time) and
 * has no `_mix_match` property is resolved by re-rendering the section
 * (Section Rendering API), which also refreshes `known`.
 *
 * "Sticky with header" (section setting): see the sticky block below.
 *
 * window.SculpdMixMatchBar exposes the pure helpers for tests.
 */
(function (root) {
  'use strict';

  // ---------------------------------------------------------------------------
  // Pure helpers
  // ---------------------------------------------------------------------------

  var SELECTION_PREFIX = 'sculpd:mm3:selection:';
  var SELECTION_TTL = 24 * 60 * 60 * 1000;

  /** Kits in the saved PDP selection for a group (0 when none / expired). */
  function selectionCount(group) {
    if (!group) return 0;
    try {
      var data = JSON.parse(root.localStorage.getItem(SELECTION_PREFIX + group) || 'null');
      if (!data || !Array.isArray(data.slots) || !data.slots.length) return 0;
      if (!(Date.now() - data.savedAt < SELECTION_TTL)) return 0;
      return data.slots.length;
    } catch (e) {
      return 0;
    }
  }

  /** The bucket for a kit count: min ≤ count ≤ max (max 0 = open-ended). */
  function bucketFor(count, buckets) {
    var match = null;
    (buckets || []).forEach(function (b) {
      var open = b.max === 0 && b.min > 0;
      if (count >= b.min && (open || count <= b.max)) {
        if (!match || b.min >= match.min) match = b;
      }
    });
    return match;
  }

  function unitFor(n, units) {
    return n === 1 ? units.one : units.other;
  }

  /** Fill the per-count placeholders, exactly as the section's Liquid does. */
  function messageFor(count, config) {
    var bucket = bucketFor(count, config.buckets);
    if (!bucket) return { text: '', bucket: null };
    var remaining = bucket.next ? Math.max(bucket.next - count, 0) : 0;
    var text = String(bucket.text || '')
      .split('[count]').join(String(count))
      .split('[remaining]').join(String(remaining))
      .split('[count_unit]').join(unitFor(count, config.units))
      .split('[remaining_unit]').join(unitFor(remaining, config.units));
    return { text: text, bucket: bucket };
  }

  /** Lowest tier qty: the end of the "below" bucket + 1, else the first open bucket's min. */
  function firstTierQty(config) {
    var first = 0;
    (config.buckets || []).forEach(function (b) {
      if (b.state === 'mid' || b.state === 'top') {
        if (!first || b.min < first) first = b.min;
      }
    });
    return first;
  }

  /**
   * Which link to show:
   *   top      the cart alone reaches the first tier (e.g. 2 kits): Checkout
   *   pending  a tier is reached only with picked kits not in the cart yet
   *   shop     otherwise
   */
  function linkFor(count, picked, config) {
    var first = firstTierQty(config);
    var cart = Math.max(0, count - picked);
    if (first && cart >= first) return 'top';
    if (first && count >= first && picked > 0) return 'pending';
    return 'shop';
  }

  /**
   * Units of the group in a /cart.js-shaped cart.
   * Returns { count, unknown } — unknown: product ids Liquid hasn't classified.
   */
  function countCart(cart, config) {
    var count = 0;
    var unknown = [];
    var known = config.known || {};
    (cart && cart.items ? cart.items : []).forEach(function (item) {
      var id = String(item.product_id);
      var props = item.properties || {};
      var member;
      if (config.group && props._mix_match === config.group) member = true;
      else if (Object.prototype.hasOwnProperty.call(known, id)) member = known[id] === true;
      else {
        unknown.push(id);
        return;
      }
      if (member) count += parseInt(item.quantity, 10) || 0;
    });
    return { count: count, unknown: unknown };
  }

  // ---------------------------------------------------------------------------
  // One bar
  // ---------------------------------------------------------------------------

  function readConfig(el) {
    var node = el.querySelector('script[data-mmab-config]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  function Bar(el) {
    this.el = el;
    this.sectionId = el.getAttribute('data-section-id');
    this.config = readConfig(el);
    this.count = this.config ? this.config.count : 0;
    this.cartCount = this.count;
    this.pendingSection = null;
  }

  /** Cart kits + the saved PDP selection. */
  Bar.prototype.update = function () {
    if (!this.config) return;
    var picked = selectionCount(this.config.group);
    this.render(this.cartCount + picked, picked);
  };

  /** picked: kits counted from the saved bundle, i.e. not in the cart yet. */
  Bar.prototype.render = function (count, picked) {
    var config = this.config;
    if (!config) return;
    this.count = count;
    var result = messageFor(count, config);
    var el = this.el;

    var msg = el.querySelector('[data-mmab-msg]');
    if (msg && msg.textContent !== result.text) msg.textContent = result.text;

    el.setAttribute('data-mmab-count', String(count));
    if (result.bucket) el.setAttribute('data-mmab-state', result.bucket.state);

    Array.prototype.forEach.call(el.querySelectorAll('[data-mmab-step]'), function (step) {
      var reached = count >= (parseInt(step.getAttribute('data-mmab-step'), 10) || 0);
      step.classList.toggle('is-reached', reached);
      var sr = step.querySelector('[data-mmab-sr]');
      if (sr) sr.textContent = reached ? config.reachedLabel || 'unlocked' : '';
    });

    var link = el.querySelector('[data-mmab-link]');
    var key = linkFor(count, picked || 0, config);
    var pending = key === 'pending';
    if (pending && !(config.links && config.links.pending && config.links.pending.label)) key = 'shop';
    var target = key && config.links ? config.links[key] : null;
    if (link && target && target.label) {
      if (link.getAttribute('href') !== target.url) link.setAttribute('href', target.url);
      if (link.textContent !== target.label) link.textContent = target.label;
    }
    if (link) {
      if (pending) link.setAttribute('data-mmab-pending', '');
      else link.removeAttribute('data-mmab-pending');
    }
  };

  // ---- "Add kits to cart →": add the saved bundle ---------------------------

  function openCartDrawer() {
    if (typeof root.SculpdOpenCart === 'function') return root.SculpdOpenCart();
    try {
      var open = function () {
        if (typeof root.upcartOpenCart === 'function') root.upcartOpenCart();
      };
      if (typeof root.upcartRefreshCart === 'function') {
        var done = root.upcartRefreshCart();
        if (done && typeof done.then === 'function') done.then(open, open);
        else setTimeout(open, 0);
      } else open();
    } catch (e) {
      /* the cart has the items anyway */
    }
  }

  /** The saved bundle as /cart/add.js items: kits (grouped by variant), then add-ons. */
  function bundleItems(group) {
    var data;
    try {
      data = JSON.parse(root.localStorage.getItem(SELECTION_PREFIX + group) || 'null');
    } catch (e) {
      data = null;
    }
    if (!data || !(Date.now() - data.savedAt < SELECTION_TTL)) return [];
    var items = [];
    var byVariant = {};
    (Array.isArray(data.slots) ? data.slots : []).forEach(function (slot) {
      if (!slot || slot.variantId == null || slot.available === false) return;
      var key = String(slot.variantId);
      if (!byVariant[key]) {
        byVariant[key] = { id: parseInt(key, 10), quantity: 0, properties: { _mix_match: group } };
        items.push(byVariant[key]);
      }
      byVariant[key].quantity += 1;
    });
    var extras = data.extras && typeof data.extras === 'object' ? data.extras : {};
    Object.keys(extras).forEach(function (id) {
      var e = extras[id];
      if (e && e.on && e.vid != null) items.push({ id: parseInt(e.vid, 10), quantity: 1, properties: { _mix_match_addon: group } });
    });
    return items;
  }

  Bar.prototype.showNote = function (text) {
    if (!text) return;
    var note = this.el.querySelector('[data-mmab-note]');
    if (!note) {
      note = document.createElement('p');
      note.className = 'mmab__note';
      note.setAttribute('data-mmab-note', '');
      note.setAttribute('role', 'status');
      this.el.appendChild(note);
    }
    note.textContent = text;
    note.hidden = false;
    var self = this;
    clearTimeout(this.noteTimer);
    this.noteTimer = setTimeout(function () {
      self.hideNote();
    }, 6000);
  };

  Bar.prototype.hideNote = function () {
    clearTimeout(this.noteTimer);
    var note = this.el.querySelector('[data-mmab-note]');
    if (note) note.hidden = true;
  };

  /** Click on "Add kits to cart →": add the saved bundle and open the cart slider. */
  Bar.prototype.onPendingClick = function (event, link) {
    var config = this.config;
    var items = config ? bundleItems(config.group) : [];
    if (!items.length || !nativeFetch) return; // nothing saved: follow the link
    event.preventDefault();
    if (this.adding) return;
    this.adding = true;
    this.hideNote();
    var self = this;
    var label = link.textContent;
    link.classList.add('is-loading');
    link.setAttribute('aria-busy', 'true');
    if (config.addingLabel) link.textContent = config.addingLabel;
    var base = (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
    nativeFetch(base + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    })
      .then(function (res) {
        return res.json().then(function (data) {
          return { ok: res.ok, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok || (result.data && result.data.status)) {
          // 422 (sold out etc.): keep the bundle so the customer can fix it.
          self.showNote((result.data && result.data.description) || config.addError);
          return;
        }
        // Added: the bundle is used up on every page (PDP box, PLP tray).
        try {
          root.localStorage.removeItem(SELECTION_PREFIX + config.group);
        } catch (e) {}
        var kits = items.filter(function (it) {
          return it.properties && it.properties._mix_match;
        }).reduce(function (n, it) {
          return n + it.quantity;
        }, 0);
        self.cartCount += kits;
        document.dispatchEvent(new root.CustomEvent('mm3:selection', { detail: { group: config.group, count: 0 } }));
        document.dispatchEvent(new root.CustomEvent('ajaxProduct:added', { detail: { product: result.data, addToCartBtn: link } }));
        openCartDrawer();
        scheduleFetch(600);
      })
      .catch(function () {
        self.showNote(config.addError);
      })
      .then(function () {
        self.adding = false;
        link.classList.remove('is-loading');
        link.removeAttribute('aria-busy');
        if (link.textContent === config.addingLabel) link.textContent = label;
        self.update();
      });
  };

  Bar.prototype.applyCart = function (cart) {
    if (!this.config || !cart || !Array.isArray(cart.items)) return;
    var result = countCart(cart, this.config);
    if (result.unknown.length) {
      this.refreshSection();
      return;
    }
    this.cartCount = result.count;
    this.update();
  };

  /** Re-render the section server-side (new kit not classified on this page). */
  Bar.prototype.refreshSection = function () {
    var self = this;
    if (!self.sectionId || !nativeFetch) return;
    if (self.pendingSection) return;
    var base = (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
    var url = base + '?sections=' + encodeURIComponent(self.sectionId);
    self.pendingSection = nativeFetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (json) {
        var html = json && json[self.sectionId];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-mmab]');
        if (!fresh) return;
        var config = readConfig(fresh);
        if (!config) return;
        // Keep everything learnt so far; the fresh render wins on conflicts.
        var known = self.config ? self.config.known || {} : {};
        Object.keys(config.known || {}).forEach(function (id) {
          known[id] = config.known[id];
        });
        config.known = known;
        self.config = config;
        self.cartCount = config.count;
        self.update();
      })
      .catch(function () {})
      .then(function () {
        self.pendingSection = null;
      });
  };

  // ---------------------------------------------------------------------------
  // Wiring (one set of listeners for every bar on the page)
  // ---------------------------------------------------------------------------

  var bars = [];
  var fallbackTimer = null;
  var cartFetch = null;
  var nativeFetch = root.fetch ? root.fetch.bind(root) : null;

  function initAll(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-mmab]'), function (el) {
      if (el.__mmab) return;
      el.__mmab = new Bar(el);
      bars.push(el.__mmab);
    });
    bars = bars.filter(function (bar) {
      return document.documentElement.contains(bar.el);
    });
  }

  function applyCart(cart) {
    clearTimeout(fallbackTimer);
    bars.forEach(function (bar) {
      bar.applyCart(cart);
    });
  }

  function fetchCart() {
    if (!nativeFetch) return;
    if (cartFetch) return;
    var base = (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
    cartFetch = nativeFetch(base + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (cart) {
        if (cart) applyCart(cart);
      })
      .catch(function () {})
      .then(function () {
        cartFetch = null;
      });
  }

  /** Re-count soon unless the theme hands over a cart first. */
  function scheduleFetch(delay) {
    clearTimeout(fallbackTimer);
    fallbackTimer = setTimeout(fetchCart, delay);
  }

  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  function watchRequests() {
    var watching = bars.some(function (bar) {
      return bar.config && bar.config.watchRequests;
    });
    if (!watching || root.__mmabWatching) return;
    root.__mmabWatching = true;

    if (nativeFetch) {
      root.fetch = function (input) {
        var promise = nativeFetch.apply(root, arguments);
        try {
          var url = typeof input === 'string' ? input : input && input.url;
          if (url && CART_WRITE.test(String(url).split('#')[0])) {
            promise.then(
              function () {
                scheduleFetch(500);
              },
              function () {}
            );
          }
        } catch (e) {}
        return promise;
      };
    }

    if (root.XMLHttpRequest) {
      var open = root.XMLHttpRequest.prototype.open;
      root.XMLHttpRequest.prototype.open = function (method, url) {
        try {
          if (url && CART_WRITE.test(String(url).split('#')[0])) {
            this.addEventListener('loadend', function () {
              scheduleFetch(500);
            });
          }
        } catch (e) {}
        return open.apply(this, arguments);
      };
    }
  }

  // ---------------------------------------------------------------------------
  // Sticky with the header (section setting "Sticky with header")
  //
  // The theme makes its header sticky through the header group wrapper
  // (.header-section { position: sticky; top: 0 }) and switches it off by
  // setting it back to relative (header setting off, mega menu taller than the
  // window) or static (short landscape screens). The bar only sticks while the
  // header really is sticky, read from its computed position:
  //
  //   bar above the header  bar at top: 0; the header moves down by the bar's
  //                         height (html.mmab-sticky-above + --mmab-sticky-h)
  //   bar below the header  bar at top: <visible header height>, re-measured
  //                         when the header compresses (headerStickyChange)
  //
  // The collection page's sticky filter bar / sidebar (offset by the header's
  // height in the theme JS) are pushed down by the bar's height too.
  // ---------------------------------------------------------------------------

  var sticky = { el: null, wrapper: null, header: null, observer: null, frame: 0, active: false };

  function headerIsSticky(header) {
    if (!header) return false;
    return root.getComputedStyle(header).position === 'sticky';
  }

  /** Bottom of what's visible of the header, relative to its group wrapper. */
  function headerVisibleHeight(header) {
    var top = header.getBoundingClientRect().top;
    var bottom = top;
    Array.prototype.forEach.call(
      header.querySelectorAll('.site-header, .site-header__element--sub'),
      function (node) {
        if (!node.offsetHeight) return;
        var style = root.getComputedStyle(node);
        if (style.visibility === 'hidden' || style.display === 'none' || parseFloat(style.opacity) === 0) return;
        var rect = node.getBoundingClientRect();
        if (rect.bottom > bottom) bottom = rect.bottom;
      }
    );
    if (bottom === top) bottom = top + header.offsetHeight;
    return Math.max(0, Math.round(bottom - top));
  }

  function clearSticky() {
    var html = document.documentElement;
    html.classList.remove('mmab-sticky-above', 'mmab-sticky-below');
    html.style.removeProperty('--mmab-sticky-h');
    if (sticky.wrapper) {
      sticky.wrapper.classList.remove('mmab-section--sticky', 'mmab-section--sticky-below');
      sticky.wrapper.style.removeProperty('--mmab-sticky-top');
    }
    if (sticky.active) {
      sticky.active = false;
      offsetCollectionFilters(0);
    }
  }

  function updateSticky() {
    sticky.frame = 0;
    if (!sticky.el || !sticky.wrapper) return;
    var html = document.documentElement;
    var header = (sticky.header = document.querySelector('.header-section'));
    var barHeight = sticky.el.offsetHeight;

    if (!header || !barHeight || !headerIsSticky(header)) {
      clearSticky();
      return;
    }

    var above = !!(sticky.wrapper.compareDocumentPosition(header) & Node.DOCUMENT_POSITION_FOLLOWING);
    sticky.wrapper.classList.add('mmab-section--sticky');
    html.style.setProperty('--mmab-sticky-h', barHeight + 'px');

    if (above) {
      sticky.wrapper.classList.remove('mmab-section--sticky-below');
      sticky.wrapper.style.setProperty('--mmab-sticky-top', '0px');
      html.classList.remove('mmab-sticky-below');
      html.classList.add('mmab-sticky-above');
    } else {
      html.classList.remove('mmab-sticky-above');
      html.classList.add('mmab-sticky-below');
      sticky.wrapper.classList.add('mmab-section--sticky-below');
      sticky.wrapper.style.setProperty('--mmab-sticky-top', headerVisibleHeight(header) + 'px');
    }
    sticky.active = true;
    offsetCollectionFilters(barHeight);
  }

  function scheduleSticky() {
    if (!sticky.el || sticky.frame) return;
    sticky.frame = (root.requestAnimationFrame || setTimeout)(updateSticky);
  }

  // Same formula as the theme's setFilterStickyPosition(), plus the bar.
  function offsetCollectionFilters(barHeight) {
    var siteHeader = document.querySelector('.site-header');
    var filterBar = document.querySelector('.collection-filter');
    if (!siteHeader || !filterBar || !(root.theme && root.theme.config && root.theme.config.stickyHeader)) return;
    var top = siteHeader.offsetHeight - 1 + barHeight;
    filterBar.style.top = top + 'px';
    var sidebar = document.querySelector('.grid__item--sidebar');
    if (sidebar) sidebar.style.top = top + 30 + 'px';
  }

  var filterTimer = null;
  function afterThemeFilters() {
    // The theme re-sets the filter offsets 500ms after these events.
    clearTimeout(filterTimer);
    filterTimer = setTimeout(function () {
      if (sticky.active && sticky.el) offsetCollectionFilters(sticky.el.offsetHeight);
    }, 600);
  }

  function setupSticky() {
    var el = document.querySelector('[data-mmab][data-mmab-sticky="true"]');
    if (sticky.observer) sticky.observer.disconnect();
    if (!el) {
      clearSticky();
      sticky.el = sticky.wrapper = null;
      return;
    }
    if (sticky.wrapper && sticky.wrapper !== el.closest('.shopify-section')) clearSticky();
    sticky.el = el;
    sticky.wrapper = el.closest('.shopify-section') || el;
    if (root.ResizeObserver) {
      sticky.observer = new ResizeObserver(scheduleSticky);
      sticky.observer.observe(el);
      var header = document.querySelector('.header-section');
      if (header) sticky.observer.observe(header);
      var siteHeader = document.querySelector('.site-header');
      if (siteHeader) sticky.observer.observe(siteHeader);
    }
    updateSticky();
  }

  document.addEventListener('headerStickyChange', function () {
    scheduleSticky();
    // The header animates between its states; measure again once it settles.
    setTimeout(scheduleSticky, 350);
    afterThemeFilters();
  });
  root.addEventListener('resize', function () {
    scheduleSticky();
    afterThemeFilters();
  });
  document.addEventListener('shopify:section:unload', function (event) {
    if (sticky.wrapper && event.target === sticky.wrapper) {
      clearSticky();
      sticky.el = sticky.wrapper = null;
    }
  });

  function updateAll() {
    bars.forEach(function (bar) {
      bar.update();
    });
  }

  function start() {
    initAll(document);
    setupSticky();
    if (!bars.length) return;
    // Liquid painted the cart count; add a saved PDP selection, if any.
    updateAll();
    watchRequests();
  }

  document.addEventListener('click', function (event) {
    var link = event.target && event.target.closest && event.target.closest('[data-mmab-link][data-mmab-pending]');
    if (!link) return;
    var el = link.closest('[data-mmab]');
    var bar = el && el.__mmab;
    if (bar) bar.onPendingClick(event, link);
  });

  // The PDP box saved / cleared its selection (this tab or another one).
  document.addEventListener('mm3:selection', updateAll);
  root.addEventListener('storage', function (event) {
    if (!event.key || event.key.indexOf(SELECTION_PREFIX) === 0) updateAll();
  });

  document.addEventListener('cart:updated', function (event) {
    var cart = event.detail && event.detail.cart;
    if (cart && Array.isArray(cart.items)) applyCart(cart);
    else scheduleFetch(0);
  });

  // The drawer's buildCart() normally follows with cart:updated; when the
  // store uses the cart page there's no drawer, so fall back to /cart.js.
  document.addEventListener('ajaxProduct:added', function () {
    scheduleFetch(800);
  });

  root.addEventListener('pageshow', function (event) {
    if (event.persisted) scheduleFetch(0);
  });

  // Theme editor: the section is re-rendered in place.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
    updateAll();
    setupSticky();
    watchRequests();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  root.SculpdMixMatchBar = {
    bucketFor: bucketFor,
    messageFor: messageFor,
    countCart: countCart,
    selectionCount: selectionCount,
    bundleItems: bundleItems,
    linkFor: linkFor,
    refresh: fetchCart,
    updateSticky: updateSticky,
    bars: function () {
      return bars.slice();
    },
  };
})(window);
